/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CategoryHierarchy as CategoryHierarchyData,
    IProductRefinerHierarchy,
    IProductsDimensionsAvailabilities
} from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getCatalogId, ICoreContext, getUrlSync, getParameterizeFeatureFlags } from '@msdyn365-commerce/core';
import {
    ChannelInventoryConfiguration,
    ProductPrice,
    ProductRefinerValue, ProductsDataActions,
    SortColumn,
    StoreOperationsDataActions,
    TextValueTranslation,
    SimpleProduct,
    UnitOfMeasureConversion,
    format,
    AttributeDataType, ProductRefinerSource,
    ProductList
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    DimensionSwatchDisplayTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    IDimensionsApp, ObjectExtensions, getCategoryUrl, getCategorySlug,
    OrderTemplate,
    QueryResultSettingsProxy,
    GetOrderTemplatesInput,
    getOrderTemplates
} from '@msdyn365-commerce-modules/retail-actions';
import { ICategoryMap } from '@msdyn365-commerce-modules/retail-actions/dist/types/get-categories-hierarchy';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    getTelemetryObject,
    ILabeledDropdownOnChangeNotification,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    isMobile,
    ITelemetryContent,
    LabeledDropdown,
    UncontrolledPagination,
    VariantType, Tooltip
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable, reaction, transaction, action, toJS, when, comparer } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { ProductListViewMode, IProductListViewModeProps } from '../../components/product-list-component/components/product-list-view-mode';

import {
    buildListPageUrl,
    getCollectionProducts,
    getRefinersForCollectionDataAction,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    parseQueryParam,
    ProductListInventoryFilteringOptions,
    RefinersByCollectionInput,
    sortOptions
} from './actions';
import { IFullProductsSearchResultsWithCount } from './actions/get-full-products-by-collection';
import { hydrateRefinersFromUrl } from './actions/url-utils';
import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion, ModalToggle,
    ProductSearchResultItems,
    SearchResultModal
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { ListPageState } from './list-page-state';
import { IDlvrySearchResultContainerData } from './dlvry-search-result-container.data';
import { IDlvrySearchResultContainerProps, preferredViewMode } from './dlvry-search-result-container.props.autogenerated';
import { ProductListComponent, ProductListProduct, productSearchResults2ProductList } from '../../components/product-list-component/product-list-component';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';
import {
    createInventoryAvailabilitySearchCriteria,
    mapAggregatedProductInventoryInformation,
    DeliveryMode,
    IProductInventoryInformation
} from "../../components/common/product-inventory-utils";
import { validateProductAccessArray, ValidateProductAccessResult } from '../../shared-utils/validate-product-access';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { getActivePricesAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { onBuyHasAccessValidator } from '../../shared-utils/attributes/on-buy-access-validator';
import { ProductSearchResultExt } from '../../shared-utils/types';
import ProductListCompactComponent from '../../components/product-list-compact-component/product-list-compact-component';
import { getAgreementProductRefinersToHide, getCustomerAgreements } from '../../shared-utils/agreement-product-utils';
import { getProductListsByProductIdsAsync, IProductListByProductId } from '../../actions/get-product-lists-by-product-ids';
import { IBrandSchemeEntity } from '../../actions/entities/brand-scheme-entity';
import { getBrandSchemesByIdsAsync } from '../../actions/get-brand-schemes.action';
import RefineCategoryTree from './components/refine-category-tree';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';

export interface ISearchResultContainerViewProps extends IDlvrySearchResultContainerProps<IDlvrySearchResultContainerData> {
    searching?: boolean;
    products?: React.ReactNode;
    totalProductCount?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    RefineAndProductSectionContainer: INodeProps;
    searchBar?: React.ReactNode;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    viewModesComponent: IProductListViewModeProps;
    selectedProductsActions?: React.ReactNode;
    productInventoryStatus?: IProductInventoryInformation[];
    simpleProducts: SimpleProduct[];
    unitConversions: UnitOfMeasureConversion[];
    brandSchemes?: IBrandSchemeEntity[];
    productQuantities: ISearchResultContainerState['productQuantities'];
    breakoffProducts: ISearchResultContainerState['breakoffProducts'];
    onUpdateQuantity: (product: { RecordId: number }, quantity: number) => void;
    onBreakoffChange: (product: { RecordId: number }, enabled: boolean) => void;
    onUpdateOrderTemplate: (product?: ProductSearchResultExt, newOrderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    refiners: IProductRefinerHierarchy[] | undefined;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    channelInventoryConfiguration?: ChannelInventoryConfiguration;
    productInventoryStatus?: IProductInventoryInformation[];
    productQuantities: { [key: number]: number };
    working: boolean;
    retrievedActivePrices: boolean;
    validatedProductAccess?: ValidateProductAccessResult[];
    showEmptySearchBarTooltip: boolean;
    simpleProducts: SimpleProduct[];
    unitConversions: UnitOfMeasureConversion[];
    breakoffProducts: { [key: number]: boolean };
    shouldShowSubCategories: boolean;
    orderTemplates?: OrderTemplate[];
    cachedCounts: { [key: string]: number };
    brandSchemes?: IBrandSchemeEntity[];
    searching?: boolean;
}

interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}

/**
 *
 * SearchResultContainer component.
 * @extends {React.PureComponent<IDlvrySearchResultContainerProps<IDlvrySearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<
    IDlvrySearchResultContainerProps<IDlvrySearchResultContainerData>,
    ISearchResultContainerState
> {

    private readonly _viewModeCookieName = 'i365_searchResultViewMode_cookie';

    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    @computed get shouldShowAvailability(): boolean {
        return !this.props.config.disableAvailabilityCheckUnauthorized || this.props.context.request.user.isAuthenticated;
    }

    private readonly sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: sortOptions.sortByOptionRelevanceDesc, value: this.props.resources.sortByOptionRelevanceDesc },
        { key: sortOptions.sortByOptionNameAsc, value: this.props.resources.sortByOptionNameAsc },
        { key: sortOptions.sortByOptionNameDesc, value: this.props.resources.sortByOptionNameDesc },
        /* Does not work with customer specific pricing */
        /* { key: sortOptions.sortByOptionPriceAsc, value: this.props.resources.sortByOptionPriceAsc },
        { key: sortOptions.sortByOptionPriceDesc, value: this.props.resources.sortByOptionPriceDesc }, */
        { key: sortOptions.sortByOptionRatingDesc, value: this.props.resources.sortByOptionRatingDesc }
        /* Removed until Microsoft improves this functionality */
        /* { key: sortOptions.sortByOptionBestSelling, value: this.props.resources.sortByOptionBestSelling },
        { key: sortOptions.sortByOptionNewScore, value: this.props.resources.sortByOptionNewScore },
        { key: sortOptions.sortByOptionTrendingScore, value: this.props.resources.sortByOptionTrendingScore } */
    ];

    private readonly _refineItemCommonProps: IRefineItemCommonProps;

    private readonly _pageType: string | undefined = this.props.config.productScopeAttributeRecordId
        ? 'Category'
        : this.props.context.request.urlTokens.pageType;

    @observable
    private _viewport: GridSize = 'lg';

    @observable
    private _viewMode: ProductListViewMode | undefined = undefined;

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly _sortAndFilterContainerRef: React.RefObject<LabeledDropdown>;

    private readonly _searchBarInputRef: React.RefObject<HTMLInputElement>;

    // private expandrefinerCount: number | undefined = 0;

    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    private readonly defaultPageNumber = 0;

    private readonly catalogId = getCatalogId(this.props.context.request);

    private isListPageStateUpdated: boolean = false;

    private _isMounted: boolean = false;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation?.Text;
    }

    public constructor(props: IDlvrySearchResultContainerProps<IDlvrySearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Check required.
        this._viewport = props.context.request?.device?.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<LabeledDropdown>();
        this._searchBarInputRef = React.createRef<HTMLInputElement>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.productPrices = [];
        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            refiners: this.props.data.refiners.result,
            channelInventoryConfiguration: undefined,
            productInventoryStatus: undefined,
            validatedProductAccess: [],
            productQuantities: {},
            working: false,
            retrievedActivePrices: false,
            showEmptySearchBarTooltip: false,
            simpleProducts: [],
            unitConversions: [],
            breakoffProducts: {},
            shouldShowSubCategories: !!this.props.data.category.result?.ParentCategory,
            cachedCounts: {}
        };
        const { placeholderTextMax, minLabel, maxLabel, rangeNameFormat } = this.props.resources;
        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumber;
        const validationErrorRange = this.props.resources.validationErrorNotRange;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax,
            minLabel,
            maxLabel,
            rangeNameFormat,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this._updateViewport();

        const savedViewMode = GetCookie(this.props.context.request.cookies, this._viewModeCookieName);
        if (savedViewMode) {
            this._viewMode = +savedViewMode;
        } else if (this.props.config.preferredViewMode) {
            switch(this.props.config.preferredViewMode) {
                case preferredViewMode.listView:
                    this._viewMode = ProductListViewMode.listView;
                    break;
                case preferredViewMode.compactView:
                    this._viewMode = ProductListViewMode.compactView;
                    break;
                case preferredViewMode.cardView:
                default:
                    this._viewMode = ProductListViewMode.cardView;
                    break;
            }
        }
    }
    public async componentDidMount(): Promise<void> {
        this._isMounted = true;

        const count = this.props.data.products.result?.count;

        if (this.props.data.products.result?.cacheKey && count) {
            this.setState({ cachedCounts: {
                [this.props.data.products.result.cacheKey]: count
            }});
        }

        if (MsDyn365.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            window.addEventListener('popstate', (event) => {
                const category: CategoryHierarchyData | undefined = event.state?.category;

                if (category) {
                    this.props.context.request.urlTokens.recordId = category.RecordId.toString();

                    transaction(() => {
                        this.props.data.category.result = category;
                        this.props.data.categoryHierarchy.result = this._buildCategoryHierarchy(category);
                        this.props.data.listPageState.result!.currentPageNumber = 0;
                    });
                }
            });
            this._updateViewport();
        }

        const listPageState = await this.props.data.listPageState;
        const products = await this.props.data.products;

        if (listPageState.searchText) {
            void GTM.searchEventAsync(listPageState.searchText);
        }

        products.count = count || 0;

        // @ts-expect-error -- window is not typed.
        if (window && !window.units) {
            // @ts-expect-error -- window is not typed.
            window.units = async (...productsId: any[]) => {
                const activeProducts = (await this.props.data.listPageState).activeProducts;
                const input = productsId.map(x => x ? x.toString() : undefined).filter(Boolean) as string[];
                const ids = activeProducts.filter(x => x.ItemId && input.includes(x.ItemId)).map(x => x.RecordId);

                if (!ids || ids.length === 0) {
                    return;
                }

                const results = await getProductUnitOfMeasureOptionsAsync({ callerContext: this.props.context.actionContext }, ids);

                console.log('results', toJS(results));

                const conversions = [...new Set(results.map(x => x.ItemId))].map(x => {
                    const defaultUOM = activeProducts.find(ap => ap.ItemId === x)?.DefaultUnitOfMeasure;
                    const conversion = results.find(r =>
                        r.ItemId === x
                        && r.FromUnitOfMeasureSymbol === defaultUOM
                        && (r.ToUnitOfMeasureSymbol === 'Kilogram' || r.ToUnitOfMeasureSymbol === 'Stykk' || r.ToUnitOfMeasureSymbol === 'Liter'));

                    return {
                        ItemId: x,
                        DefaultUnitOfMeasure: defaultUOM,
                        Conversion: conversion?.ToUnitOfMeasureSymbol,
                        Value: conversion?.Factor,
                        TextValue: conversion ? `${conversion?.Factor} ${conversion?.ToUnitOfMeasureSymbol}` : undefined
                    };
                });

                return toJS(conversions);
            };
        }

        this._updateListPageState(listPageState, products);

        if (this.props.context.actionContext.requestContext?.user.isAuthenticated) {
            void this._getSimpleProducts();
            void this._getProductsInProductLists();
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- check is required.
        if (
            this.props.config.enableAffiliationBasedPricing &&
            this.props.context.actionContext.requestContext?.user?.isAuthenticated
        ) {
            if (products.products.some(x => !x.PrefetchedPrice)) {
                void this._getActivePrice();
            } else {
                this.setState({ retrievedActivePrices: true });
            }
        }

        await this._updateVariantAvailability();

        void this._getUnitOfMeasureConversions();
        void this._getBrandSchemes();

        const channelInventoryConfiguration = await StoreOperationsDataActions.getInventoryConfigurationAsync({
            callerContext: this.props.context.actionContext
        });
        if (this._isMounted) {
            this.setState({ channelInventoryConfiguration });
        }

        let productInventoryStatus: IProductInventoryInformation[] | undefined;

        try {
            productInventoryStatus = await this._getAvailableQuantity();
        } catch {
            productInventoryStatus = this.props.data.listPageState.result?.activeProducts.map((p) => {
                return {
                    IsProductAvailable: false,
                    StockLevelCode: 'ERR',
                    StockLevelLabel: this.props.resources.availabilityError,
                    ProductAvailableQuantity: {
                        ProductId: p.RecordId
                    }
                };
            });
        }

        const validatedProductAccess = validateProductAccessArray(
            this.props.data.customerInformation.result?.Attributes,
            this.props.data.listPageState.result?.activeProducts);
        this.setState({ productInventoryStatus, validatedProductAccess });

        void when(
            () => this.props.data.category.status === 'SUCCESS',
            () => {
                window.history.replaceState({ category: this.props.data.category.result }, '');
            }
        );

        void this.props.data.orderTemplates.then((orderTemplates) => {
            this.setState({ orderTemplates });
        });

        // Initialize reaction based on listPageState properties
        reaction(
            () => [
                toJS(listPageState.activeFilters),
                listPageState.currentPageNumber ?? 0,
                listPageState.searchText ?? '',
                toJS(listPageState.sortingCritera?.Columns),
                this.props.data.category.result?.RecordId ?? 0,
                this.props.data.category.result?.ParentCategory ?? 0
            ],
            async () => {
                this.setState({ searching: true });

                const newListPageState = await this.props.data.listPageState;
                const input = new GetFullProductsByCollectionInput(
                    newListPageState.pageType,
                    this.props.context.request.apiSettings,
                    {
                        Paging: {
                            Top: this.props.config.itemsPerPage,
                            Skip: Math.max(newListPageState.pageSize * (newListPageState.currentPageNumber ?? 0), 0)
                        },
                        count: true,
                        Sorting: newListPageState.sortingCritera ?? {}
                    },
                    newListPageState.activeFilters ?? [],
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.
                    +(this.props.context.request.urlTokens.recordId || 0),
                    (this.props.context.request.query?.productId || this.props.context.request.query?.recommendation)
                        ? JSON.stringify({
                            ProductId: this.props.context.request.query?.productId,
                            Recommendation: this.props.context.request.query?.recommendation,
                            SearchText: listPageState.searchText
                        })
                        : listPageState.searchText,
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required check.
                    !!this.props?.config?.includeAttributes,
                    this.props.config.updateRefinerPanel,
                    this.props.context.request.locale,
                    this.catalogId,
                    channelInventoryConfiguration,
                    undefined,
                    this.props.config.productScopeAttributeRecordId,
                    this.props.config.productScopeAttributeValue
                );

                const productResults = await getCollectionProducts(input, this.props.context.actionContext);
                newListPageState.totalProductCount = productResults?.count;
                if (this.state.cachedCounts[input.getCacheKey()]) {
                    newListPageState.totalProductCount = this.state.cachedCounts[input.getCacheKey()];
                }
                newListPageState.activeProducts = productResults?.products;

                const newCachedCounts = { ...this.state.cachedCounts };
                newCachedCounts[input.getCacheKey()] = newListPageState.totalProductCount;
                this.setState({ cachedCounts: newCachedCounts });

                if (this.props.context.request.user.isAuthenticated) {
                    void this._getSimpleProducts();
                    void this._getProductsInProductLists();
                }

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- check is required.
                if (
                    this.props.config.enableAffiliationBasedPricing &&
                    this.props.context.actionContext.requestContext?.user?.isAuthenticated
                ) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call api to retrieve the availabilities data without blocking the UI.
                    this._getActivePrice();
                }

                void this._getUnitOfMeasureConversions();
                void this._getBrandSchemes();

                await this._updateVariantAvailability();

                if (this.props.config.updateRefinerPanel) {
                    const refinerInput = new RefinersByCollectionInput(
                        newListPageState.pageType,
                        this.props.context.request.apiSettings,
                        {
                            Paging: {
                                Top: this.props.config.itemsPerPage,
                                Skip: Math.max(
                                    newListPageState.pageSize * (newListPageState.currentPageNumber ?? this.defaultPageNumber),
                                    this.defaultPageNumber
                                )
                            },
                            count: true,
                            Sorting: newListPageState.sortingCritera ?? {}
                        },
                        newListPageState.activeFilters ?? [],
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.
                        +(this.props.context.request.urlTokens.recordId || 0),
                        (this.props.context.request.query?.productId || this.props.context.request.query?.recommendation)
                            ? JSON.stringify({
                                ProductId: this.props.context.request.query?.productId,
                                Recommendation: this.props.context.request.query?.recommendation,
                                SearchText: listPageState.searchText
                            })
                            : listPageState.searchText,
                        false,
                        this.props.config.updateRefinerPanel,
                        undefined,
                        this.catalogId,
                        channelInventoryConfiguration,
                        undefined,
                        this.props.config.productScopeAttributeRecordId,
                        this.props.config.productScopeAttributeValue
                    );

                    const refiners = await getRefinersForCollectionDataAction(refinerInput, this.props.context.actionContext);
                    this.setState({ refiners, shouldShowSubCategories: !!this.props.data.category.result?.ParentCategory });
                }

                this.setState({ searching: false });
            },
            { delay: 1000, equals: comparer.structural }
        );

        reaction(
            () => listPageState.activeProducts.map(p => p.RecordId) ?? [],
            async () => {
                let productInventoryStatus: IProductInventoryInformation[] | undefined;

                try {
                    productInventoryStatus = await this._getAvailableQuantity();
                } catch {
                    productInventoryStatus = this.props.data.listPageState.result?.activeProducts.map((p) => {
                        return {
                            IsProductAvailable: false,
                            StockLevelCode: 'ERR',
                            StockLevelLabel: this.props.resources.availabilityError,
                            ProductAvailableQuantity: {
                                ProductId: p.RecordId
                            }
                        };
                    });
                }

                const validatedProductAccess = validateProductAccessArray(
                    this.props.data.customerInformation.result?.Attributes,
                    this.props.data.listPageState.result?.activeProducts);
                this.setState({ productInventoryStatus, validatedProductAccess });
            },
            { equals: comparer.structural }
        );
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public render(): JSX.Element {
        const {
            config: { imageSettings, className },
            resources,
            data: { listPageState, products: productCollection, featureProduct }
        } = this.props;

        if (!this.isListPageStateUpdated && listPageState.result && productCollection.result) {
            this._updateListPageState(listPageState.result, productCollection.result);
            this.isListPageStateUpdated = true;
        }

        //const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const searchProduct = listPageState.result?.featureProduct ?? featureProduct.result ?? null;
        let errorText = '';
        // if (!ArrayExtensions.hasElements(products)) {
        if (listPageState.result && listPageState.result.activeProducts.length === 0) {
            errorText = this._pageType === 'Category' ? resources.resultCategoryNotFoundText : resources.resultSearchNotFoundText;
        }
        const productsComponent = this._getProducts();
        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
                recommendation={this.props.context.request.query?.recommendation}
            />
        );

        const viewModeProps: IProductListViewModeProps = {
            viewMode: this._viewMode,
            cardButtonTxt: resources.viewModeCardText,
            listButtonTxt: resources.viewModeListText,
            compactButtonTxt: resources.viewModeCompact,
            setCardViewMode: this.setCardViewMode,
            setListViewMode: this.setListViewMode,
            setCompactViewMode: this.setCompactViewMode
        };

        const searchResultContainerViewProps: Partial<ISearchResultContainerViewProps> = {
            ...this.props,
            searching: this.state.searching,
            products: productsComponent,
            totalProductCount: this._getTotalProductCount(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container', className)
            },
            sortByOptions: this.props.data.listPageState.result?.activeProducts.length !== 0 ? this._getSortingDropDown() : undefined,
            pagination: this._getPagination(),
            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: { className: 'ms-search-result-container__product-section' },
            RefineAndProductSectionContainer: { className: 'ms-search-result-container__refine-product-section' },
            searchBar: this._getSearchBar(),
            choiceSummary: this._getChoiceSummary(),
            modalToggle:
            <ModalToggle
                text={resources.modalTitle}
                ariaLabel={resources.modalTitle}
                innerRef={this._modalToggleRef}
                onClick={this._toggleModal}
                id='search-result-modal'
            />,
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct: searchProduct ? featureComponent : null,
            productPrices: this.productPrices,
            productsDimensionAvailabilities: this.state.productsDimensionAvailabilities,
            viewModesComponent: viewModeProps,
            productInventoryStatus: this.state.productInventoryStatus,
            simpleProducts: this.state.simpleProducts,
            unitConversions: this.state.unitConversions,
            brandSchemes: this.state.brandSchemes,
            productQuantities: this.state.productQuantities,
            breakoffProducts: this.state.breakoffProducts,
            onUpdateQuantity: this._onUpdateQuantity,
            onBreakoffChange: this._onBreakoffChange,
            onUpdateOrderTemplate: this._onUpdateOrderTemplates,
            retrieveOrderTemplates: this._retrieveOrderTemplates
        };
        return this.props.renderView(searchResultContainerViewProps as any) as React.ReactElement;
    }

    /**
     * Get products variants dimension availabilities.
     * @returns Void.
     */
    private readonly _updateVariantAvailability = async (): Promise<void> => {
        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        if (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none) &&
            this.props.config.includeAttributes
        ) {
            await this._getProductsDimensionsAvailabilities();
        }
    };

    public readonly setCompactViewMode = () => {
        this._viewMode = ProductListViewMode.compactView;
        this._updateCurrentViewMode();
    };

    public readonly setListViewMode = () => {
        this._viewMode = ProductListViewMode.listView;
        this._updateCurrentViewMode();
    };

    public readonly setCardViewMode = () => {
        this._viewMode = ProductListViewMode.cardView;
        this._updateCurrentViewMode();
    };

    private readonly _updateCurrentViewMode = () => {
        const cookieAge: number = 31536000000;
        const newCookieValue: string = String(this._viewMode);
        SetCookie(this.props.context.request.cookies, newCookieValue, this._viewModeCookieName, cookieAge, true);
    };

    /**
     * Update the list page state with initial data.
     * @param listPageState -- List page State.
     * @param products -- Products.
     */
    @action
    private readonly _updateListPageState = (listPageState: ListPageState, products: IFullProductsSearchResultsWithCount): void => {
        let querySorting: SortColumn[] = [];
        const defaultPageNumber: number = 10;

        if (this._pageType === 'Category') {
            listPageState.pageType = 'Category';
        } else {
            listPageState.pageType = 'Search';
        }

        if (this.props.context.request.query?.sorting) {
            querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        }

        listPageState.currentPageNumber =
            (this.props.context.request.query?.skip &&
                +this.props.context.request.query.skip / (this.props.config.itemsPerPage ?? defaultPageNumber)) ||
            0;
        listPageState.sortingCritera = { Columns: querySorting };
        listPageState.pageSize = this.props.config.itemsPerPage ?? defaultPageNumber;
        listPageState.activeProducts = products.products;
        listPageState.totalProductCount = products.count;
        listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request) || [];
        listPageState.searchText = this.props.context.request.query?.q || '';
        if (this.props.context.request.query?.productId) {
            const featureProduct = this.props.data.featureProduct.result;
            if (featureProduct) {
                listPageState.featureProduct = featureProduct;
            }
        }
    };

    /**
     * Get products dimension availabilities.
     * @returns Void.
     */
    private readonly _getProductsDimensionsAvailabilities = async (): Promise<void> => {
        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];

        const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
            new DimensionAvailabilitiesForProductSearchResultInput(
                productsList,
                this.props.context as ICoreContext<IDimensionsApp>,
                DimensionSwatchDisplayTypes.productCard
            ),
            this.props.context.actionContext
        );

        if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
            this.setState({ productsDimensionAvailabilities });
        }
    };

    /**
     * Get the active price for the products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        // Hide prices when retrieving so we don't show base price
        this.setState({ retrievedActivePrices: false });

        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];
        const missingPrices = productsList.filter((x) => !x.PrefetchedPrice);

        if (missingPrices.length > 0) {
            try {
                const prices = await getActivePricesAsync(
                    { callerContext: this.props.context.actionContext },
                    {
                        CatalogId: this.catalogId,
                        ChannelId: this.props.context.request.apiSettings.channelId
                    },
                    missingPrices.map((x) => x.RecordId),
                    new Date(),
                    null,
                    undefined,
                    true
                );
                const listPageState = await this.props.data.listPageState;

                prices.forEach((p) => {
                    const indexOfProduct = listPageState.activeProducts.findIndex((x) => x.RecordId === p.ProductId);

                    if (indexOfProduct !== -1) {
                        listPageState.activeProducts[indexOfProduct].Price = p.CustomerContextualPrice || 0;
                        listPageState.activeProducts[indexOfProduct].BasePrice = p.AdjustedPrice || 0;
                        listPageState.activeProducts[indexOfProduct].Badge = getDiscountBadge(p, listPageState.activeProducts[indexOfProduct].FallbackBadge);
                        listPageState.activeProducts[indexOfProduct].ProductPrice = p;
                        listPageState.activeProducts[indexOfProduct].PrefetchedPrice = true;
                    }
                });
            } catch (error) {
                missingPrices.forEach((product) => {
                    product.PriceFailed = true;
                });
                console.error('Failed to retrieve active prices.', error);
            }
        }

        this.setState({ retrievedActivePrices: true });
    };

    private readonly _getSimpleProducts = async(): Promise<void> => {
        const missing = this.props.data.listPageState.result?.activeProducts
            ?.filter(x => !this.state.simpleProducts.find(y => y.RecordId === x.RecordId));

        if (!missing) {
            return;
        }

        const simpleProducts = await getByIdsAsync(
            {callerContext:this.props.context.actionContext},
            this.props.context.request.apiSettings.channelId,
            missing?.map(x => x.RecordId)
        );

        const newSimpleProducts = [...this.state.simpleProducts ];

        simpleProducts.forEach(x => {
            if (!newSimpleProducts.find(y => y.RecordId === x.RecordId)) {
                newSimpleProducts.push(x);
            }
        });

        this.setState({ simpleProducts: newSimpleProducts });
    };

    private readonly _getProductsInProductLists = async(): Promise<void> => {
        const { context, data } = this.props;
        const activeProducts = (await data.listPageState).activeProducts;
        const ids = activeProducts.map(x => x.RecordId);

        if (ids.length === 0) {
            return;
        }

        const results = await getProductListsByProductIdsAsync({ callerContext: context.actionContext }, ids);

        activeProducts.forEach(product => {
            const lists = results.filter(x => x.ProductId === product.RecordId);
            if (lists.length > 0) {
                product.PartOfProductLists = lists;
            }
        });
    };

    private readonly _getUnitOfMeasureConversions = async (): Promise<void> => {
        const { actionContext, request, app } = this.props.context;

        if (!request.user.isAuthenticated || app.config.unitOfMeasureConversions !== true) {
            return;
        }

        const activeProducts = (await this.props.data.listPageState).activeProducts;
        const ids = activeProducts.map(x => x.RecordId);

        if (!ids || ids.length === 0) {
            return;
        }

        const unitConversions = await getProductUnitOfMeasureOptionsAsync({ callerContext: actionContext }, ids);

        this.setState({ unitConversions });
    };

    private readonly _getBrandSchemes = async (): Promise<void> => {
        const { actionContext } = this.props.context;

        const activeProducts = (await this.props.data.listPageState).activeProducts;
        const brandSchemesIds = ArrayExtensions.unique(activeProducts.flatMap(x => x.SpecialBadges?.split('|')).filter(Boolean) || []) as string[];

        if (!brandSchemesIds || brandSchemesIds.length === 0) {
            return;
        }

        const result = await getBrandSchemesByIdsAsync({ callerContext: actionContext }, brandSchemesIds);

        this.setState({ brandSchemes: result });
    };

    private readonly _getSearchResultModal = (): ISearchResultModalViewProps => {
        const { resources } = this.props;
        return SearchResultModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonText,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal
        });
    };

    private readonly _getSortingDropDown = (): ISortByViewProps => {
        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        let dropdownOptions = this._getAllSortByOptions();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- App config settings are of "any" value.
        if (this.props.context.app.config.hideRating) {
            dropdownOptions = dropdownOptions.filter(dropdownOption => dropdownOption.key !== sortOptions.sortByOptionRatingDesc);
        }

        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={dropdownOptions}
                selectedOption={activeDropdown}
                onSelectOption={this._updateSortByDropdown}
                ref={this._sortAndFilterContainerRef}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown
        };
    };

    private readonly _getPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        // eslint-disable-next-line max-len -- existing code
        const skipCount = !ObjectExtensions.isNullOrUndefined(listPageState?.currentPageNumber)
            ? listPageState!.currentPageNumber * (this.props.config.itemsPerPage || 10)
            : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = listPageState?.totalProductCount || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private readonly _buildCategoryHierarchy = (n: CategoryHierarchy) => {
        const categories = this.props.data.categories.result;

        if (!categories) {
            return [];
        }

        const categoryHierarchy: CategoryHierarchy[] = [];
        categoryHierarchy.push(n);
        let parentCategory = n.ParentCategory ? categories.find(x => x.RecordId === n.ParentCategory) : undefined;
        while (parentCategory) {
            if (parentCategory.ParentCategory) {
                categoryHierarchy.unshift(parentCategory);
                parentCategory = categories.find(x => x.RecordId === parentCategory!.ParentCategory);
            } else {
                break;
            }
        }

        return categoryHierarchy;
    };

    private readonly _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context } = this.props;
        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        const validRefiners =
            this.state.refiners && ArrayExtensions.hasElements(this.state.refiners)
                ? this.state.refiners.filter(refiner => {
                    return ArrayExtensions.hasElements(refiner.Values) && !this._shouldHideFromRefiners(refiner);
                })
                : data.refiners.result &&
                data.refiners.result.filter(refiner => {
                    return ArrayExtensions.hasElements(refiner.Values) && !this._shouldHideFromRefiners(refiner);
                  });

        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        // this._getexpandRefinerCount(validRefiners && validRefiners.length, config.expandRefinersCount);
        // const expandedRefiners = this.expandrefinerCount;
        const subMenus =
            validRefiners &&
            validRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={false}
                        shouldHide={(refiner: IProductRefinerHierarchy, selectedRefinerValues: ProductRefinerValue[]) => {
                            if (refiner.SourceValue !== ProductRefinerSource.Category) {
                                return false;
                            }

                            const selectedValuesOfRefiner = refiner.Values.find(
                                x => selectedRefinerValues.find(y => y.RefinerRecordId === x.RefinerRecordId)
                            );

                            return !!selectedValuesOfRefiner;
                        }}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        telemetryContent={this.telemetryContent}
                        productCountAriaLabel={this.props.resources.productCountAriaLabel}
                        refineItemAriaLabel={this.props.resources.refineItemAriaLabel}
                        refineItemsAriaLabel={this.props.resources.refineItemsAriaLabel}
                        singleProductCountAriaLabel={this.props.resources.singleProductCountAriaLabel}
                        isMobile={this.isMobile}
                        yesText={this.props.resources.yes}
                        noText={this.props.resources.no}
                        transitRefinerRecordId={this.props.config.transitRefinerRecordId}
                        noResultsFilterValuesText={this.props.resources.noResultsFilterValuesText}
                    />
                );
            });

        const categories = this.props.data.categories.result;
        const currentCategory = this.props.data.category.result;
        let rootCategory = categories?.find(x => !x.ParentCategory);

        if (categories && rootCategory) {
            rootCategory = { ...rootCategory };
            const categoryMap: ICategoryMap = {};
            const locale = this.props.context.request.locale;
            const childCategories = categories.filter(x => x.ParentCategory === rootCategory?.RecordId);

            for (const category of childCategories) {
                const localName = category.NameTranslations?.find(x => x.Language === locale)?.Text
                    || category.NameTranslations?.[0]?.Text
                    || category.Name;
                const categoryHierarchy: CategoryHierarchy = { ...category };
                categoryHierarchy.NeutralizedName = category.Name;
                categoryHierarchy.Name = localName || categoryHierarchy.NeutralizedName;
                categoryMap[category.RecordId] = categoryHierarchy;
            }

            categoryMap[rootCategory.RecordId] = rootCategory;

            rootCategory.Url = getCategoryUrl(rootCategory, this.props.context.actionContext, categoryMap);

            for (const category of Object.values(categoryMap)) {
                category.Url = getCategoryUrl(category, this.props.context.actionContext, categoryMap);
            }

            const relevantCategories = (this.state.refiners?.find(x => x.SourceValue === 2)?.Values
                ?.map(x => x.RefinerRecordId)
                ?.filter(Boolean) || []) as number[];
            const relevantCategoriesAndParents: number[] = [];

            relevantCategories.forEach(x => {
                relevantCategoriesAndParents.push(x);
                const category = categories.find(y => y.RecordId === x);
                const parents: number[] = [];

                if (category) {
                    let parentCategory = categories.find(y => y.RecordId === category.ParentCategory);
                    if (!parentCategory) {
                        return;
                    }
                    while (parentCategory) {
                        parents.push(parentCategory.RecordId);
                        parentCategory = categories.find(y => y.RecordId === parentCategory!.ParentCategory);
                        if (!parentCategory) {
                            break;
                        }
                    }
                }

                relevantCategoriesAndParents.push(...parents);
            });
            const relevantCategoriesObjects = categories.filter(x => relevantCategoriesAndParents && relevantCategoriesAndParents?.includes(x.RecordId));

            subMenus?.unshift((
                <RefineCategoryTree
                    label={this.props.resources.refinerCategoryTitle}
                    categories={relevantCategoriesObjects}
                    currentCategory={currentCategory}
                    onSelectCategory={(category, selected) => {
                        if (selected) {
                            this.props.context.request.urlTokens.recordId = category.RecordId.toString();
                            this.props.data.category.result = category;
                            this.props.data.categoryHierarchy.result = this._buildCategoryHierarchy(category);

                            const parameterizeFeatureFlags = getParameterizeFeatureFlags(this.props.context.actionContext);
                            category.Slug = getCategorySlug(category, [], parameterizeFeatureFlags);
                            category.Url = getUrlSync('category', this.props.context.actionContext, { category });

                            // Selecting another main category, clear all refiners
                            const fullUrl = new URL(category.Url!, getCurrentUrl(this.props.context.request));
                            this.props.data.listPageState.result!.currentPageNumber = 0;

                            const activeFilters = this.props.data.listPageState.result?.activeFilters || [];
                            window.history.pushState({ category: JSON.parse(JSON.stringify(category)) }, '', buildListPageUrl(fullUrl, activeFilters));
                        } else {
                            this._clearCategory();
                        }
                    }}
                />
            ));
        }

        return {
            RefineMenuContainer: { className: 'ms-refine-menu' },
            RefinerSectionContainer: { className: 'ms-search-result-container__refiner-section' },
            refiners: subMenus
        };
    };

    /**
     * Should hide from refiners.
     * @param refiner - The refiner.
     * @returns ShouldHideFromRefiners.
     */
    private readonly _shouldHideFromRefiners = (refiner: IProductRefinerHierarchy): boolean => {
        const { channelInventoryConfiguration } = this.state;

        // Hide currency filter
        if (refiner.RecordId === 0 && refiner.DataTypeValue === AttributeDataType.Currency) {
            return true;
        }

        // Hide all category refiners
        if (refiner.RecordId === 0 && refiner.SourceValue === ProductRefinerSource.Category) {
            return true;
        }

        const refinerIds = [
            Number(this.props.context.app.config?.blockedSvalbardAttributeRecordId),
            Number(this.props.context.app.config?.forsvaretAttributeRecordId),
            Number(this.props.context.app.config?.prioritySortingAttribute),
            Number(this.props.config.productScopeAttributeRecordId),
            Number(this.props.config.alcoholRefinerRecordId),
            Number(this.props.config.tobaccoRefinerRecordId),
            Number(this.props.config.cocacolaRefinerRecordId),
            Number(this.props.config.bamaRefinerRecordId),
            Number(this.props.config.dutyFreeSugarRefinerRecordId),
            Number(this.props.config.norturaRefinerRecordId)
        ];

        const agreementProductAttributeRecordIds = getAgreementProductRefinersToHide(this.props.context, this.props.data.orgInformation.result);

        if (agreementProductAttributeRecordIds && agreementProductAttributeRecordIds.length > 0) {
            refinerIds.push(...agreementProductAttributeRecordIds);
        }

        const transitRefinerRecordId = this.props.config.transitRefinerRecordId;
        const customerTransit = transitRefinerRecordId
            ? this.props.data.orgInformation.result?.Attributes?.find(x => x.RecordId === Number(transitRefinerRecordId))
            : undefined;
        const customerTransitValue = Number(customerTransit?.AttributeValue?.StringValue);

        if (isNaN(customerTransitValue) || customerTransitValue === 0) {
            refinerIds.push(Number(transitRefinerRecordId));
        }

        // Always hide sortable attribute since it's just for sorting products in Azure search, not for customers
        return (
            refiner.RecordId === this.props.data.products.result?.inventoryAwareSortableAttributeId ||
            refiner.RecordId === channelInventoryConfiguration?.ProductAvailabilitySortableAttributeRecordId ||
            refiner.RecordId && refinerIds.includes(refiner.RecordId) ||
            (this.props.context.app.config.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS &&
                (refiner.RecordId === this.props.data.products.result?.channelInventoryConfigurationId ||
                    refiner.RecordId === channelInventoryConfiguration?.InventoryProductAttributeRecordId))
        );
    };

    private readonly _canSearchEmpty = (): Boolean => {
        // If we're on a recommendation page, we can search for empty string. Will show entire recommendation list
        if (this.props.context.request.query?.recommendation) {
            return true;
        }
        // If we're on a category page, we can search for empty string. Will show entire category
        if (this._pageType === 'Category') {
            return true;
        }
        return false;
    };

    private readonly _getSearchBar = (): React.ReactNode => {
        const {
            data: { listPageState },
            resources: { searchPlaceholderText }
        } = this.props;

        const onKeyDown = async (e: React.KeyboardEvent) => {
            this.setState({ showEmptySearchBarTooltip: false });

            if (e.key === 'Enter') {
                const value = (e.target as HTMLInputElement).value;

                if (!value && !this._canSearchEmpty()) {
                    if (this._pageType !== 'Category') {
                        const rootCategory = this.props.data.categories.result?.find(x => !x.ParentCategory);

                        if (rootCategory) {
                            const rootCategorySlug = getCategorySlug(rootCategory, []);
                            const rootCategoryUrl = getUrlSync('category', this.props.context.actionContext, { category: rootCategory })
                                ?.replace('/undefined', `${rootCategorySlug}/`);

                            if (rootCategoryUrl) {
                                window.location.href = rootCategoryUrl;
                                return;
                            }
                        }
                    }

                    // Can't search for empty string
                    this.setState({ showEmptySearchBarTooltip: true });
                    return;
                }

                const newUrl = new URL(window.location.href);
                if (value) {
                    newUrl.searchParams.set('q', value);
                } else {
                    newUrl.searchParams.delete('q');
                }
                // Reset to first page everytime we do a search
                newUrl.searchParams.delete('skip');
                if (this.props.context.request.query?.skip) {
                    delete this.props.context.request.query.skip;
                }
                window.history.pushState({ pathname: newUrl.href }, '', newUrl.href);

                transaction(() => {
                    listPageState.result!.currentPageNumber = 0;
                    listPageState.result!.searchText = value;
                });

                if (value) {
                    void GTM.searchEventAsync(value);
                }
            }
        };

        return (
            <div className='msc-search-bar__wrapper'>
                <div className='msc-search-bar__container'>
                    <button
                        className='msc-search-bar__button'
                        title={this.props.resources.searchText}
                        onClick={() => {
                            const event = new KeyboardEvent('keydown', {
                                key: 'Enter',
                                code: 'Enter',
                                keyCode: 13,
                                charCode: 13,
                                bubbles: true,
                                cancelable: true
                            } as any);

                            this._searchBarInputRef.current!.dispatchEvent(event);
                        }} />
                    <input
                        ref={this._searchBarInputRef}
                        className='msc-search-bar__input'
                        placeholder={searchPlaceholderText}
                        defaultValue={this.props.context.request.query?.q}
                        onKeyDown={onKeyDown}
                        onBlur={() => this.setState({ showEmptySearchBarTooltip: false })}
                    />
                    <button
                        className='msc-search-bar__clear'
                        title={this.props.resources.clearAllText}
                        onClick={() => {
                            const newUrl = new URL(window.location.href);
                            newUrl.searchParams.delete('q');
                            this._searchBarInputRef.current!.value = '';
                            window.history.pushState({ pathname: newUrl.href }, '', newUrl.href);
                            transaction(() => {
                                listPageState.result!.currentPageNumber = 0;
                                listPageState.result!.searchText = '';
                            });
                        }}
                    />
                    <Tooltip
                        isOpen={this.state.showEmptySearchBarTooltip}
                        target='.msc-search-bar__container'
                        className='msc-search-bar__tooltip'
                    >
                        {this.props.resources.searchEmptySearchTermText}
                    </Tooltip>
                </div>
            </div>
        );
    };

    private readonly _getChoiceSummary = (): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const { channelInventoryConfiguration } = this.state;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        const refinerValueName = this.state.refiners?.find(rating => rating.KeyName === 'Rating');

        return (
            <ChoiceSummary
                classNames='ms-choice-summary-by-category'
                clearAllText={resources.clearAllText}
                label={resources.choiceSummaryLabel}
                closeAriaLabel={resources.closeAriaLabel}
                selectedChoices={selectedRefiners}
                category={this.props.data.category.result}
                refinerHierarchy={this.state.refiners || []}
                choiceFormat={resources.choiceFormat}
                choiceRangeValueFormat={resources.choiceRangeValueFormat}
                telemetry={telemetry}
                onChoiceClicked={this._onChoiceClicked}
                urlBuilder={this._buildRefinerUrlForChoiceSummary}
                choiceAriaLabel={resources.choiceAriaLabel}
                telemetryContent={this.telemetryContent}
                context={this.props.context}
                channelInventoryConfigurationId={channelInventoryConfiguration?.InventoryProductAttributeRecordId}
                refinerValues={refinerValueName}
                yesLabel={resources.yes}
                noLabel={resources.no}
                transitRefinerRecordId={this.props.config.transitRefinerRecordId}
            />
        );
    };
    private readonly _getAvailableQuantity = async (): Promise<IProductInventoryInformation[] | undefined> => {
        const { context } = this.props;
        const products = (await this.props.data.listPageState).activeProducts;

        if (this.shouldShowAvailability === false) {
            return undefined;
        }

        if (!products || products.length === 0){
            return undefined;
        }

        const productIds = products.map(p =>  p.RecordId);
        const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
            context.actionContext,
            productIds,
            true,
            undefined,
            undefined,
            DeliveryMode.shipping
        );
        const shippingAvailability = await ProductsDataActions.getEstimatedAvailabilityAsync(
            { callerContext: context.actionContext },
            shippingSearchCriteria
        );

        if (shippingAvailability.ProductWarehouseInventoryAvailabilities) {
            const shippingInventory = mapAggregatedProductInventoryInformation(context.actionContext, shippingAvailability);
            if (ArrayExtensions.hasElements(shippingInventory)) {
                shippingInventory.forEach((x) => {
                    x.deliveryType = DeliveryMode.shipping;
                    x.ProductAvailableQuantity.ExtensionProperties = shippingAvailability.ProductWarehouseInventoryAvailabilities
                        ?.find(y => y.ProductId === x.ProductAvailableQuantity.ProductId)?.ExtensionProperties;
                });
                return shippingInventory;
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    };
    /**
     * Get Active products.
     * @returns - JSX Elements.
     */
    private readonly _getProducts = (): JSX.Element => {
        const {
            context,
            config: { imageSettings, allowBackNavigation: shouldAllowBackNavigation },
            data
        } = this.props;
        // const { resources } = this.props;
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const resources = {
            ...this.props.resources,
            buyNowButtonText: this.props.resources.addToCartButtonText,
            addToWishlistText: this.props.resources.addToWishlistButtonText,
            removeButtonText: this.props.resources.removeFromWishlistButtonText
        };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Reading config value.
        const breadCrumbType: string = this.props.context.app.config.breadcrumbType;
        const shouldAllowBack = shouldAllowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
        const quickviewSlot = this.props.slots?.quickview?.[0];
        const productComparisonButton = ArrayExtensions.hasElements(this.props.slots.productComparisonButton)
            ? this.props.slots.productComparisonButton[0]
            : null;

        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        if (this._viewMode === ProductListViewMode.cardView) {
            return (
                <ProductSearchResultItems
                    cart={this.props.data.cart.result}
                    products={products}
                    simpleProducts={this.state.simpleProducts}
                    orgInformation={this.props.data.orgInformation.result}
                    customerInformation={this.props.data.customerInformation.result}
                    productQuantities={this.state.productQuantities}
                    breakoffProducts={this.state.breakoffProducts}
                    onUpdateQuantity={this._onUpdateQuantity}
                    onBreakoffChange={this._onBreakoffChange}
                    unitConversions={this.state.unitConversions}
                    brandSchemes={this.state.brandSchemes}
                    productInventoryStatus={this.state.productInventoryStatus|| undefined}
                    wishlists={this.props.data.wishlists.result}
                    context={this.props.context}
                    config={this.props.config}
                    imageSettings={imageSettings}
                    resources={resources}
                    moduleType={this.props.typeName}
                    moduleId={this.props.id}
                    allowBackNavigation={shouldAllowBack}
                    disableBuyButtons={!this.state.retrievedActivePrices}
                    showPrices={this.props.data.products.result?.products.every(x => x.PrefetchedPrice) || this.state.retrievedActivePrices}
                    telemetryContent={this.telemetryContent}
                    quickviewSlot={quickviewSlot}
                    productComparisonButton={productComparisonButton}
                    channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                    isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                    productsDimensionAvailabilities={this.state.productsDimensionAvailabilities}
                    validatedProductAccess={this.state.validatedProductAccess}
                    productInventoryInformation={this.state.productInventoryStatus}
                    onUpdateOrderTemplate={this._onUpdateOrderTemplates}
                    retrieveOrderTemplates={this._retrieveOrderTemplates}
                />
            );
        }

        const productList = productSearchResults2ProductList(
            products,
            this.props.data.products.result?.channelInventoryConfigurationId,
            this.props.data.cart.result,
            this.state.productInventoryStatus || undefined
        );

        productList.forEach(product => {
            product.CurrentQuantity = this.state.productQuantities[product.RecordId] || 0;
        });

        if (this._viewMode === ProductListViewMode.compactView) {
            return (
                <ProductListCompactComponent
                    context={this.props.context}
                    moduleId={this.props.id}
                    moduleType={this.props.typeName}
                    resources={resources}
                    showPrices
                    showTotalPrices
                    products={productList}
                    simpleProducts={this.state.simpleProducts}
                    customerInformation={this.props.data.customerInformation.result}
                    organizationInformation={this.props.data.orgInformation.result}
                    cart={this.props.data.cart.result}
                    unitConversions={this.state.unitConversions}
                    wishlists={this.props.data.wishlists.result}
                    imageSettings={imageSettings}
                    onBuyValidate={(product) => onBuyHasAccessValidator(
                        this.props.context,
                        this.props.data.orgInformation.result,
                        this.props.data.listPageState.result?.activeProducts.find(p => p.RecordId === product.RecordId) as ProductSearchResultExt,
                        this.props.resources.toastNoAccessToProduct
                    )}
                    breakoffProducts={this.state.breakoffProducts}
                    onUpdateQuantity={this._onUpdateQuantity}
                    onBreakoffChange={this._onBreakoffChange}
                    onUpdateOrderTemplates={this._onUpdateOrderTemplates}
                    retrieveOrderTemplates={this._retrieveOrderTemplates}
                />
            );
        }

        return (
            <ProductListComponent
                cart={this.props.data.cart.result}
                products={productList}
                simpleProducts={this.state.simpleProducts}
                customerInformation={this.props.data.customerInformation.result}
                customerAgreements={getCustomerAgreements(context, data.orgInformation.result?.Attributes)}
                unitConversions={this.state.unitConversions}
                brandSchemes={this.state.brandSchemes}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                productComparisonButton={productComparisonButton}
                validatedProductAccess={this.state.validatedProductAccess}
                actionsAvailable={{
                    addToCartAction: true,
                    addToWishlistAction: true,
                    productComparisonButton:false
                }}
                disableBuyButtons={this.state.working || !this.state.retrievedActivePrices}
                showAvailability={this.shouldShowAvailability}
                showPrices={this.props.data.products.result?.products.every(x => x.PrefetchedPrice) || this.state.retrievedActivePrices}
                showTotalPrices
                showCartCount
                wishlists={this.props.data.wishlists.result}
                smallerImage
                breakoffProducts={this.state.breakoffProducts}
                onUpdateQuantity={this._onUpdateQuantity}
                onBreakoffChange={this._onBreakoffChange}
                onUpdateOrderTemplates={this._onUpdateOrderTemplates}
                onBuyValidate={(product) => onBuyHasAccessValidator(
                    this.props.context,
                    this.props.data.orgInformation.result,
                    this.props.data.listPageState.result?.activeProducts.find(p => p.RecordId === product.RecordId) as ProductSearchResultExt,
                    this.props.resources.toastNoAccessToProduct
                )}
                retrieveOrderTemplates={this._retrieveOrderTemplates}
            />
        );
    };

    private readonly _onUpdateQuantity = (product: { RecordId: number }, quantity: number) => {
        if (!product.RecordId) {
            return;
        }

        this.setState((prevState) => {
            const productQuantities = { ...prevState.productQuantities };
            productQuantities[product.RecordId] = quantity;
            return { productQuantities };
        });
    };

    private readonly _onBreakoffChange = (product: { RecordId: number }, enabled: boolean) => {
        if (!product.RecordId) {
            return;
        }

        this.setState((prevState) => ({
            breakoffProducts: ({ ...prevState.breakoffProducts, [product.RecordId]: enabled })
        }));
    };

    private readonly _onUpdateOrderTemplates = async (product?: ProductSearchResultExt | ProductListProduct, newOrderTemplate?: ProductList): Promise<OrderTemplate[]> => {
        const { context, config } = this.props;
        const { orderTemplates } = this.state;
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(orderTemplates ? orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }

            if (product) {
                const listPageState = await this.props.data.listPageState;
                const newProducts = [...listPageState.activeProducts];
                const newProduct = newProducts?.find(x => x.RecordId === product.RecordId);

                if (newProduct) {
                    const newPartOfProductList: IProductListByProductId = {
                        ProductId: newProduct.RecordId,
                        ProductListId: newOrderTemplate?.Id,
                        Name: newOrderTemplate?.Name
                    };
                    if (!newProduct.PartOfProductLists) {
                        newProduct.PartOfProductLists = [];
                    }
                    newProduct.PartOfProductLists.push(newPartOfProductList);

                    listPageState.activeProducts = newProducts;
                }
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: context.request,
                config: config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        this.setState({ orderTemplates: newOrderTemplates });
        return newOrderTemplates;
    };

    private readonly _retrieveOrderTemplates = async (): Promise<OrderTemplate[]> => {
        return this.state.orderTemplates ? this.state.orderTemplates : this._onUpdateOrderTemplates();
    };

    private readonly _getTotalProductCount = (): React.ReactNode => {
        const {
            data: { listPageState, products }
        } = this.props;

        let totalProductCount: number | undefined;
        if (listPageState && listPageState.result && listPageState.result.totalProductCount !== undefined) {
            totalProductCount = listPageState.result.totalProductCount;
        } else if (products && products.result) {
            totalProductCount = products.result.count;
        }

        return (
            <div className={`ms-search-result-container__total-product-count`}>
                {format(this.props.resources.numberOfSearchResults, totalProductCount || 0)}
            </div>
        );
    };

    private readonly _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            // eslint-disable-next-line max-len -- existing code
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        window.history.pushState(
            {},
            '',
            buildListPageUrl(
                getCurrentUrl(this.props.context.request),
                undefined,
                [this._getSortColumnFromSelectedOption(notification.selectedOption)],
                undefined
            )
        );
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: [this._getSortColumnFromSelectedOption(notification.selectedOption)]
            };
        });
    };

    private readonly _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;

        if (!data.searchConfiguration.result) {
            return {};
        }

        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });

        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }

        return {};
    };

    /**
     * Retrieves current sort criteria.
     * @returns Sort criteria column which is currently active or undefined if it's not specified.
     */
    private readonly _getSortCriteriaColumn = (): SortColumn | undefined => {
        const sortCriteria = this.props.data.listPageState.result?.sortingCritera;
        if (ArrayExtensions.hasElements(sortCriteria?.Columns)) {
            return sortCriteria!.Columns[0];
        }

        const queryColumns = parseQueryParam<SortColumn[]>('sorting', this.props.context.request);
        if (ArrayExtensions.hasElements(queryColumns)) {
            return queryColumns[0];
        }

        return undefined;
    };

    /**
     * Retrieves dropdown options.
     * @returns All dropdown options.
     */
    private readonly _getAllSortByOptions = (): ILabeledDropdownOption[] => {
        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!searchConfiguration) {
            return [];
        }

        const mappedSearchConfiguration = this.sortByDropdownOptions.filter(mappedSearchConfig =>
            searchConfiguration.find(item => mappedSearchConfig.key === item.key)
        );

        return ArrayExtensions.validValues(mappedSearchConfiguration);
    };

    /**
     * Retrieves current selected dropdown option based on the sort criteria.
     * @returns Current dropdown option.
     */
    private readonly _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const activeSortColumn = this._getSortCriteriaColumn();

        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!activeSortColumn || !searchConfiguration) {
            return undefined;
        }

        const activeMappedConfig = searchConfiguration.find(
            mappedSearchConfig =>
                mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
        );
        if (!activeMappedConfig) {
            return undefined;
        }

        return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
    };

    private readonly _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: 
                ${!requestContext ? 'requestContext ' : ''} ${!actionContext ? 'actionContext ' : ''} could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Server side the list PageState will not be called.
        const updatedRefinementCriteria = getUpdatedRefinementCriteria(
            itemToggleNotification,
            this.props.data.listPageState.result?.activeFilters || []
        );

        window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private readonly _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                this.props.data.listPageState.result.activeFilters || []
            );

            return buildListPageUrl(getCurrentUrl(this.props.context.request), newRefinementCriteria);
        }
        this.props.telemetry.warning('[buildRefinerQueryString]List Page State Not available, unable to build refiner URL');
        return '';
    };

    private readonly _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        const { data, telemetry } = this.props;

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (notification.clearAll) {
            // Set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = dropdownElementId && document.getElementById(dropdownElementId);
            setTimeout(() => {
                dropdownElement && dropdownElement.focus();
            }, 50);

            this._clearCategory(true);
        } else if (notification.clearCategory) {
            this._clearCategory();
            // Return as the rest gets handled in _clearCategory
            return;
        } else {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );

            const fullUrl = getCurrentUrl(this.props.context.request);
            window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria));
            data.listPageState.result.currentPageNumber = 0;
            data.listPageState.result.activeFilters = currentRefinementCriteria;
        }
    };

    private readonly _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _getViewport(): GridSize {
        if (MsDyn365.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }
        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }

    private _toggleModal(): void {
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    // private readonly _getexpandRefinerCount = (validRefiners?: number, refinerCount?: expandRefinersCount): void => {
    //     switch (refinerCount) {
    //         case expandRefinersCount.all:
    //             this.expandrefinerCount = validRefiners;
    //             break;
    //         case expandRefinersCount.one:
    //             this.expandrefinerCount = 1;
    //             break;
    //         case expandRefinersCount.two:
    //             this.expandrefinerCount = 2;
    //             break;
    //         case expandRefinersCount.three:
    //             this.expandrefinerCount = 3;
    //             break;
    //         case expandRefinersCount.four:
    //             this.expandrefinerCount = 4;
    //             break;
    //         default:
    //             this.expandrefinerCount = 0;
    //     }
    // };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    private readonly _getRootProductCategory = (category: CategoryHierarchy): CategoryHierarchy | undefined => {
        if (category.ParentCategory) {
            const parent = this.props.data.categories.result?.find(x => x.RecordId === category.ParentCategory)!;
            if (!parent.ParentCategory) {
                return category;
            }
            return this._getRootProductCategory(parent);
        } else {
            return category;
        }
    };

    private readonly _clearCategory = (clearFilters?: boolean): void => {
        // If clearing a root product category (not all products root category), go to root category (all products)
        // If clearing a sub category, go to nearest root product category
        // If clearing all filters, go to root category (all products)
        const currentCategory = this.props.data.category.result;
        const parentCategory = currentCategory?.ParentCategory && this.props.data.categories.result?.find(x => x.RecordId === currentCategory.ParentCategory);
        let newCategory = clearFilters || (parentCategory && !parentCategory.ParentCategory)
            ? this.props.data.categories.result?.find(x => !x.ParentCategory)
            : this._getRootProductCategory(currentCategory!);

        if (newCategory) {
            newCategory = { ...newCategory };
        } else {
            return;
        }

        // Since only one category can be selected at a time, deselecting a category should go back to the root category
        this.props.context.request.urlTokens.recordId = newCategory.RecordId.toString();
        this.props.data.category.result = newCategory;
        this.props.data.categoryHierarchy.result = this._buildCategoryHierarchy(newCategory);

        // Root category wont have URL so we need to build it
        const parameterizeFeatureFlags = getParameterizeFeatureFlags(this.props.context.actionContext);
        newCategory.Slug = getCategorySlug(newCategory, [], parameterizeFeatureFlags);
        newCategory.Url = getUrlSync('category', this.props.context.actionContext, { category: newCategory });

        // Deselecting means going back to root category which will contain any non-category refiners
        const fullUrl = new URL(newCategory.Url!, getCurrentUrl(this.props.context.request));
        const newActiveFilters = clearFilters
            ? []
            : this.props.data.listPageState.result?.activeFilters?.filter(x => x.RefinerSourceValue !== ProductRefinerSource.Category) || [];
        this.props.data.listPageState.result!.currentPageNumber = 0;
        this.props.data.listPageState.result!.activeFilters = newActiveFilters;

        window.history.pushState({ category: newCategory }, '', buildListPageUrl(fullUrl, newActiveFilters));
    };
}
